import { useState } from 'react';
import { Play, RotateCcw, Square, Workflow } from 'lucide-react';
import { api, unwrap, unwrapPaged } from '@/lib/api';
import { useLocale } from '@/i18n/locale-provider';
import { useAsync, usePolling } from '@/lib/use-async';
import { useAuth } from '@/lib/auth';
import {
  Badge, Card, EmptyState, ErrorPanel, Kpi, PageHeader, Spinner, Table, Td, statusTone,
} from '@/components/ui';

interface Configuration {
  id: string;
  name: string;
  nameAr: string | null;
  description: string | null;
  descriptionAr: string | null;
  scheduleMode: string;
  scenarioCode: string | null;
  intervalMinutes: number;
  isDefault: boolean;
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  controls: Record<string, unknown>;
}

interface Run {
  id: string;
  runNumber: string;
  status: string;
  trigger: string;
  scenarioCode: string | null;
  durationMs: number | null;
  createdAt: string;
  errorMessage: string | null;
  totals: {
    customersCreated: number; ordersCreated: number; ordersCancelled: number;
    paymentsSucceeded: number; paymentsFailed: number; invoicesIssued: number;
    returnsRaised: number; grossValue: number; errors: number;
  };
}

interface RunEvent {
  id: string;
  sequence: number;
  type: string;
  level: 'INFO' | 'WARN' | 'ERROR';
  message: string;
  personaCode: string | null;
  createdAt: string;
}

interface Scenario {
  code: string;
  name: string;
  nameAr: string | null;
  description: string;
  descriptionAr: string | null;
}

export function AgentPage() {
  const { t, n, money, dateTime, fill, field } = useLocale();
  const { can } = useAuth();
  const writable = can('agent:write');

  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [scenario, setScenario] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const configurations = useAsync(() => unwrap<Configuration[]>(api.agent.get('/agent/configurations')));
  const scenarios = useAsync(() => unwrap<Scenario[]>(api.agent.get('/agent/catalogue/scenarios')));
  // Poll while a run may be in flight.
  const runs = usePolling(() => unwrapPaged<Run>(api.agent.get('/agent/runs?pageSize=15')), 5000);
  const stats = usePolling(
    () => unwrap<{ runs: Record<string, number>; lastFiftyRuns: Record<string, number>; runnerEnabled: boolean }>(
      api.agent.get('/agent/statistics'),
    ),
    15000,
  );
  const events = useAsync(
    () => (selectedRun
      ? unwrapPaged<RunEvent>(api.agent.get(`/agent/runs/${selectedRun}/events?pageSize=200`))
      : Promise.resolve({ items: [], total: 0, page: 1, pageSize: 0, totalPages: 0 })),
    [selectedRun],
  );

  const defaultConfig = configurations.data?.find((c) => c.isDefault) ?? configurations.data?.[0];

  async function startRun() {
    if (!defaultConfig) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.agent.post('/agent/runs', {
        configurationId: defaultConfig.id,
        ...(scenario ? { scenarioCode: scenario } : {}),
      });
      // The run is created immediately and continues in the background.
      window.setTimeout(runs.reload, 400);
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  async function act(runId: string, action: 'stop' | 'retry') {
    setBusy(true);
    setActionError(null);
    try {
      await api.agent.post(`/agent/runs/${runId}/${action}`);
      runs.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  if (configurations.loading && !configurations.data) return <Spinner />;
  if (configurations.error) {
    return (
      <>
        <PageHeader title={t.agent.title} />
        <ErrorPanel error={configurations.error} onRetry={configurations.reload}
          title={t.agent.unreachable} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t.agent.title}
        subtitle={t.agent.subtitle}
        actions={
          writable ? (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={scenario}
                onChange={(event) => setScenario(event.target.value)}
                className="input !w-auto !py-1.5 text-sm"
                aria-label={t.agent.scenario}
              >
                <option value="">{t.agent.noScenario}</option>
                {scenarios.data?.map((option) => (
                  <option key={option.code} value={option.code}>{field(option, 'name')}</option>
                ))}
              </select>
              <button type="button" onClick={startRun} disabled={busy || !defaultConfig} className="btn-primary">
                <Play size={15} aria-hidden /> {t.agent.startRun}
              </button>
            </div>
          ) : (
            <Badge tone="neutral">{fill(t.common.readOnly, { permission: 'agent:write' })}</Badge>
          )
        }
      />

      {actionError && (
        <div className="mb-5"><ErrorPanel error={actionError} title={t.agent.actionFailed} /></div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t.agent.runsCompleted} value={stats.data ? n(stats.data.runs.completed) : null} />
        <Kpi label={t.agent.runsFailed} value={stats.data ? n(stats.data.runs.failed) : null}
             tone={stats.data?.runs.failed ? 'bad' : 'good'} />
        <Kpi label={t.agent.ordersLast50} value={stats.data ? n(stats.data.lastFiftyRuns.ordersCreated) : null} />
        <Kpi label={t.agent.valueGenerated} value={stats.data ? money(stats.data.lastFiftyRuns.grossValue) : null} />
      </div>

      {stats.data && !stats.data.runnerEnabled && (
        <p className="mt-4 rounded-md border border-info-border bg-info-bg p-3 text-sm text-info-fg">
          {t.agent.schedulerDisabled}
        </p>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title={t.agent.configurations}>
          <Table head={[t.agent.name, t.agent.schedule, t.agent.scenario, t.agent.lastRun]} empty={!configurations.data?.length}>
            {configurations.data?.map((config) => (
              <tr key={config.id} className="hover:bg-mist-50">
                <Td>
                  <p className="flex items-center gap-2 font-medium text-mist-800">
                    {field(config, 'name')}
                    {config.isDefault && <Badge tone="info">{t.agent.default}</Badge>}
                  </p>
                  {field(config, 'description') && (
                    <p className="mt-0.5 text-xs text-mist-500">{field(config, 'description')}</p>
                  )}
                </Td>
                <Td className="text-mist-600">
                  {config.scheduleMode}
                  {['INTERVAL', 'CONTINUOUS', 'SCENARIO'].includes(config.scheduleMode) && (
                    <span className="text-mist-400"> · {config.intervalMinutes}m</span>
                  )}
                </Td>
                <Td className="text-xs text-mist-600">{config.scenarioCode ?? '—'}</Td>
                <Td className="text-xs text-mist-500">{dateTime(config.lastRunAt)}</Td>
              </tr>
            ))}
          </Table>
        </Card>

        <Card title={t.agent.recentRuns} action={
          <button type="button" onClick={runs.reload} className="btn-ghost !py-1 !text-xs">{t.common.refresh}</button>
        }>
          <Table head={[t.agent.run, t.agent.status, t.agent.orders, t.agent.value, '']} empty={!runs.data?.items.length}>
            {runs.data?.items.map((run) => (
              <tr
                key={run.id}
                className={`cursor-pointer hover:bg-mist-50 ${selectedRun === run.id ? 'bg-deep-50' : ''}`}
                onClick={() => setSelectedRun(run.id === selectedRun ? null : run.id)}
              >
                <Td>
                  <p className="ltr-embed font-mono text-xs text-mist-700">{run.runNumber}</p>
                  <p className="mt-0.5 text-xs text-mist-500">
                    {dateTime(run.createdAt)}
                    {run.scenarioCode && <> · {run.scenarioCode}</>}
                  </p>
                </Td>
                <Td>
                  <Badge tone={statusTone(run.status)}>{run.status}</Badge>
                  {run.durationMs !== null && (
                    <span className="tabular ms-2 text-xs text-mist-500">{n(run.durationMs)}ms</span>
                  )}
                </Td>
                <Td className="tabular text-end text-mist-700">{n(run.totals?.ordersCreated ?? 0)}</Td>
                <Td className="tabular text-end text-mist-700">{money(run.totals?.grossValue ?? 0)}</Td>
                <Td>
                  {writable && (
                    <div className="flex justify-end gap-1">
                      {run.status === 'RUNNING' && (
                        <button
                          type="button"
                          onClick={(event) => { event.stopPropagation(); act(run.id, 'stop'); }}
                          className="rounded p-1.5 text-mist-500 hover:bg-danger-bg hover:text-danger-fg"
                          aria-label={fill(t.agent.stopRun, { run: run.runNumber })}
                        >
                          <Square size={13} />
                        </button>
                      )}
                      {(run.status === 'FAILED' || run.status === 'STOPPED') && (
                        <button
                          type="button"
                          onClick={(event) => { event.stopPropagation(); act(run.id, 'retry'); }}
                          className="rounded p-1.5 text-mist-500 hover:bg-deep-50 hover:text-deep-600"
                          aria-label={fill(t.agent.retryRun, { run: run.runNumber })}
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      <div className="mt-5">
        <Card title={selectedRun ? t.agent.decisionLog : t.agent.decisionLogPrompt}>
          {!selectedRun ? (
            <EmptyState
              icon={Workflow}
              title={t.agent.decisionLogEmpty}
              detail={t.agent.decisionLogHint}
            />
          ) : events.loading ? (
            <Spinner label={t.agent.loadingDecisions} />
          ) : (
            <ol className="max-h-[28rem] space-y-1.5 overflow-y-auto">
              {events.data?.items.map((event) => (
                <li
                  key={event.id}
                  className={`flex gap-3 rounded-md border-s-2 px-3 py-2 text-sm ${
                    event.level === 'ERROR'
                      ? 'border-danger-solid bg-danger-bg'
                      : event.level === 'WARN'
                        ? 'border-warning-solid bg-warning-bg'
                        : 'border-mist-300 bg-mist-50'
                  }`}
                >
                  <span className="tabular w-8 shrink-0 text-xs text-mist-400">{event.sequence}</span>
                  <span className="ltr-embed w-44 shrink-0 font-mono text-xs text-deep-600">{event.type}</span>
                  <span className="min-w-0 flex-1 text-mist-700">{event.message}</span>
                  {event.personaCode && (
                    <span className="ltr-embed shrink-0 text-xs text-glow-700">{event.personaCode}</span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </>
  );
}
